import React, { useRef } from 'react';

const ARROW_BUTTON =
  'shrink-0 h-11 w-11 rounded-full bg-white border border-gray-200 shadow-sm flex items-center justify-center text-xl font-extrabold text-blue-600 hover:bg-gray-50';

/**
 * Scrollable strip of story tiles (picture, "n. Title", coins) with left/right arrows. Rendered
 * inside the list card in StoriesView, so it has no chrome of its own. `stories` are the row's
 * lessons; a lesson has no picture of its own, so every tile shows `emoji`. A title that already
 * carries its number ("Balgopal Story 1") isn't numbered again. `showCoins={false}` leaves the coin
 * badge off the tiles.
 */
export default function StoryTabsBar({ stories, activeStoryId, onSelectStory, showCoins = true, emoji = '📖' }) {
  const scrollRef = useRef(null);

  const scrollBy = (amount) => {
    scrollRef.current?.scrollBy({ left: amount, behavior: 'smooth' });
  };

  return (
    <div className="flex items-center gap-3">
      <button onClick={() => scrollBy(-320)} aria-label="Scroll stories left" className={ARROW_BUTTON}>
        ←
      </button>

      {/* pb-3 leaves room for the active tile's pointer, which would otherwise be clipped by the scroller. */}
      <div
        ref={scrollRef}
        className="flex-1 flex items-stretch gap-3 overflow-x-auto scroll-smooth pt-1 pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {stories.map((story, index) => {
          const isActive = story.id === activeStoryId;
          return (
            <button
              key={story.id}
              onClick={() => onSelectStory(story.id)}
              aria-current={isActive ? 'true' : undefined}
              className={`relative shrink-0 w-[150px] flex flex-col items-center gap-1.5 rounded-2xl px-3 py-3 text-center font-extrabold transition-colors ${
                isActive
                  ? 'bg-amber-50 border-2 border-orange-400 text-slate-900 shadow-sm'
                  : 'bg-indigo-50 border-2 border-transparent text-slate-700 hover:border-indigo-200'
              }`}
            >
              <span className="text-4xl leading-none" aria-hidden="true">
                {emoji}
              </span>
              <span className="flex-1 flex items-center text-sm leading-tight break-words max-w-full">
                {/\d/.test(story.title) ? story.title : `${index + 1}. ${story.title}`}
              </span>
              {showCoins && story.coins > 0 && (
                <span className="flex items-center gap-1 rounded-full bg-amber-100 text-amber-700 px-2.5 py-0.5 text-xs font-extrabold">
                  🪙 {story.coins}
                </span>
              )}
              {isActive && (
                <span
                  aria-hidden="true"
                  className="absolute left-1/2 top-full -translate-x-1/2 h-0 w-0 border-l-[9px] border-r-[9px] border-t-[10px] border-l-transparent border-r-transparent border-t-orange-400"
                />
              )}
            </button>
          );
        })}
      </div>

      <button onClick={() => scrollBy(320)} aria-label="Scroll stories right" className={ARROW_BUTTON}>
        →
      </button>
    </div>
  );
}
